import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { useSurveyResponseManager } from '../hooks/useSurveyResponseManager';
import {
  SurveyResponseLayout,
  WelcomeScreen,
  QuestionRenderer,
  Navigation,
  CompletionScreen,
} from '../components';
import { usePostHog } from '../../../hooks/usePostHog';
import { SURVEY_RESPONSE_EVENTS } from '../../../utils/trackingEvents';

export const SurveyResponsePage: React.FC = () => {
  const { instanceId, surveyId } = useParams<{ instanceId: string; surveyId: string }>();
  const navigate = useNavigate();
  const { captureEvent } = usePostHog();

  // Check if required parameters exist early
  useEffect(() => {
    if (!instanceId || !surveyId) {
      navigate('/404');
    }
  }, [instanceId, surveyId, navigate]);

  const {
    surveyState,
    navigationState,
    currentQuestion,
    surveyData,
    isLoading,
    error,
    startSurvey,
    goToNextQuestion,
    goToPreviousQuestion,
    submitAnswer,
    updateAnswerLocally,
    completeSurvey,
    changeLanguage,
    validateCurrentAnswer,
    canSubmitResponse,
    hasCompletedSurvey,
  } = useSurveyResponseManager(instanceId || '', surveyId || '');

  // Track link access on page load
  useEffect(() => {
    if (instanceId && surveyData) {
      captureEvent(SURVEY_RESPONSE_EVENTS.LINK_ACCESSED, {
        instance_id: instanceId,
        survey_id: surveyData.id,
        survey_title: surveyData.title,
        access_method: 'direct_link',
        user_agent: navigator.userAgent,
      });
    }
  }, [instanceId, surveyData, captureEvent]);

  // Track survey abandonment on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        instanceId &&
        surveyState.hasStarted &&
        !surveyState.isCompleted &&
        surveyState.sessionId
      ) {
        captureEvent(SURVEY_RESPONSE_EVENTS.SURVEY_ABANDONED, {
          instance_id: instanceId,
          session_id: surveyState.sessionId,
          abandonment_point: surveyState.currentQuestionIndex,
          total_questions: surveyState.totalQuestions,
          questions_answered: Object.keys(surveyState.responses).length,
          completion_percentage:
            (Object.keys(surveyState.responses).length / surveyState.totalQuestions) * 100,
          abandonment_reason: 'page_unload',
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [instanceId, surveyState, captureEvent]);

  // Check if user has already completed this survey
  useEffect(() => {
    if (hasCompletedSurvey && !surveyState.isCompleted) {
      // Already completed, show completion screen
      // You might want to navigate to a different completion URL
    }
  }, [hasCompletedSurvey, surveyState.isCompleted]);

  // Early return if required parameters are missing
  if (!instanceId || !surveyId) {
    return null;
  }

  const getErrorMessage = (
    error: unknown,
    language: 'en' | 'no'
  ): { title: string; message: string } => {
    // Check for survey closed error
    if (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      error.code === 'SURVEY_INSTANCE_CLOSED'
    ) {
      return language === 'no'
        ? {
            title: 'Undersøkelsen er stengt',
            message: 'Denne undersøkelsen aksepterer ikke lenger svar. Svarperioden er utløpt.',
          }
        : {
            title: 'Survey Closed',
            message: 'This survey is no longer accepting responses. The response period has ended.',
          };
    }

    // Default error
    if (language === 'no') {
      return {
        title: 'Undersøkelsen kunne ikke lastes',
        message:
          'Undersøkelsen kan være utløpt eller ikke lenger tilgjengelig. Vennligst kontakt avsender hvis problemet vedvarer.',
      };
    } else {
      return {
        title: 'Survey could not be loaded',
        message:
          'The survey may have expired or is no longer available. Please contact the sender if the problem persists.',
      };
    }
  };

  const handleAnswerChange = (value: string | string[] | number | boolean | undefined) => {
    if (!currentQuestion) return;

    // Update answer locally without submitting to API
    const currentAnswer = surveyState.responses[currentQuestion.id];
    updateAnswerLocally(currentQuestion.id, value, currentAnswer?.comment);
  };

  const handleCommentChange = (comment: string) => {
    if (!currentQuestion) return;

    // Update comment locally without submitting to API
    const currentAnswer = surveyState.responses[currentQuestion.id];
    updateAnswerLocally(currentQuestion.id, currentAnswer?.value, comment);
  };

  const handleNext = async () => {
    if (!currentQuestion) return;

    const validation = validateCurrentAnswer();
    if (!validation.isValid) {
      return;
    }

    // Submit current answer before navigating
    const currentAnswer = surveyState.responses[currentQuestion.id];
    if (currentAnswer) {
      try {
        await submitAnswer(currentQuestion.id, currentAnswer.value, currentAnswer.comment);
        goToNextQuestion();
      } catch (error) {
        console.error('Failed to submit answer:', error);
        // Handle error (show notification, etc.)
      }
    } else {
      goToNextQuestion();
    }
  };

  const handleComplete = async () => {
    if (!currentQuestion) return;

    const validation = validateCurrentAnswer();
    if (!validation.isValid) {
      return;
    }

    // Submit current answer before completing
    const currentAnswer = surveyState.responses[currentQuestion.id];
    if (currentAnswer) {
      try {
        await submitAnswer(currentQuestion.id, currentAnswer.value, currentAnswer.comment);
        await completeSurvey();
      } catch (error) {
        console.error('Failed to submit answer or complete survey:', error);
        // Handle error
      }
    } else {
      try {
        await completeSurvey();
      } catch (error) {
        console.error('Failed to complete survey:', error);
        // Handle error
      }
    }
  };

  const renderContent = () => {
    // Loading state
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4, // Use padding instead of fixed height
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography>
            {surveyState.language === 'no' ? 'Laster undersøkelse...' : 'Loading survey...'}
          </Typography>
        </Box>
      );
    }

    // Completion state - check this BEFORE error state
    if (surveyState.isCompleted && surveyData) {
      return <CompletionScreen survey={surveyData} language={surveyState.language} />;
    }

    // Error state - but don't show error if survey is completed
    if (error || !surveyData || (!canSubmitResponse && !surveyState.isCompleted)) {
      const { title, message } = getErrorMessage(error, surveyState.language);

      // Track error occurrence
      if (instanceId && error) {
        captureEvent(SURVEY_RESPONSE_EVENTS.ERROR_ENCOUNTERED, {
          instance_id: instanceId,
          error_type: 'page_load_error',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          survey_accessible: !!surveyData,
          can_submit_response: canSubmitResponse,
          survey_completed: surveyState.isCompleted,
        });
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            p: 4,
            gap: 2,
          }}
        >
          <ErrorOutline sx={{ fontSize: 48, color: 'error.main' }} />
          <Typography variant="h6">{title}</Typography>
          <Typography sx={{ maxWidth: 400, color: 'text.secondary' }}>{message}</Typography>
          <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
            {surveyState.language === 'no' ? 'Prøv igjen' : 'Try again'}
          </Button>
        </Box>
      );
    }

    // Welcome state (not started)
    if (!surveyState.hasStarted) {
      return (
        <WelcomeScreen
          survey={surveyData}
          language={surveyState.language}
          onStart={startSurvey}
          estimatedTimeMinutes={0.5} // 30 seconds default
        />
      );
    }

    // Question state
    if (currentQuestion) {
      const currentAnswer = surveyState.responses[currentQuestion.id];
      const validation = validateCurrentAnswer();
      const errorMessage = validation.errors[0]?.message;

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            // Remove fixed minHeight to allow natural content flow
            gap: 2, // Add gap between question and navigation
          }}
        >
          <QuestionRenderer
            question={currentQuestion}
            value={currentAnswer?.value}
            comment={currentAnswer?.comment}
            language={surveyState.language}
            onChange={handleAnswerChange}
            onCommentChange={handleCommentChange}
            disabled={surveyState.isSubmitting}
            error={errorMessage}
          />

          <Navigation
            onNext={handleNext}
            onBack={goToPreviousQuestion}
            onComplete={handleComplete}
            canGoBack={navigationState.canGoBack}
            canGoNext={navigationState.canGoNext}
            canComplete={navigationState.canComplete}
            isSubmitting={surveyState.isSubmitting}
            language={surveyState.language}
          />
        </Box>
      );
    }

    return null;
  };

  return (
    <SurveyResponseLayout
      language={surveyState.language}
      onLanguageChange={changeLanguage}
      showProgress={surveyState.hasStarted && !surveyState.isCompleted}
      currentStep={surveyState.currentQuestionIndex}
      totalSteps={surveyState.totalQuestions}
    >
      {renderContent()}
    </SurveyResponseLayout>
  );
};
